import { FC } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ArrowCounterClockwiseIcon, WarningCircleIcon } from '@phosphor-icons/react';

interface AppErrorFallbackProps {
  error?: Error;
}

export const AppErrorFallback: FC<AppErrorFallbackProps> = observer(({ error }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header — matches MainHeader */}
      <Box
        sx={{
          height: '3.25rem',
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: 'white',
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/logo.svg"
          alt="logo"
          sx={{ height: 32, width: 'auto', objectFit: 'contain' }}
        />
      </Box>

      {/* Centered error card */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Accent strip */}
          <Box sx={{ height: 4, backgroundColor: theme.palette.error.main }} />

          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: `${theme.palette.error.main}12`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.error.main,
              }}
            >
              <WarningCircleIcon size={32} weight="duotone" />
            </Box>

            {/* Text */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="textLG_semiBold" sx={{ color: 'text.primary', display: 'block' }}>
                Something went wrong
              </Typography>
              <Typography
                variant="textSM_regular"
                sx={{ color: 'text.tertiary', mt: 1, display: 'block' }}
              >
                An unexpected error occurred. Refreshing the page usually fixes this.
              </Typography>
            </Box>

            {/* Error detail (dev only) */}
            {error?.message && (
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: theme.palette.grey[50],
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Typography
                  variant="textXS_regular"
                  sx={{ color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {error.message}
                </Typography>
              </Box>
            )}

            {/* Action */}
            <Button
              variant="contained"
              startIcon={<ArrowCounterClockwiseIcon size={16} />}
              onClick={() => window.location.reload()}
              sx={{ minWidth: 160 }}
            >
              Reload page
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
